import { normalizeSitePath } from "../../shared/utils/book-edition-key.utils";
import { joinPathSearchAndHash } from "../../shared/utils/filter-redirect-url.utils";
import { Site } from "../../shared/models/site.model";
import { FilterState } from "../../state/state-models";
import { applyDefaults, getStateAsLocationQuery } from "../../state/state-url-management";

const TRACKED_FILTER_KEYS = new Set(["a", "b", "c", "d", "e", "f", "g", "k", "l", "m", "n", "o", "s", "t", "v", "w", "book"]);

export type BuildVerifiedLinkHrefParams = {
    targetPath: string;
    pathname: string;
    search: string;
    sitePath: string;
    isCustomDomain: boolean;
    filterState: FilterState;
    site: Site | null | undefined;
    bookPath: string | undefined;
    bookSerializeToUrl: boolean;
};

function getCurrentLayoutSegment(pathname: string, sitePath: string): string {
    const withoutSitePath = sitePath && pathname.startsWith(sitePath)
        ? pathname.substring(sitePath.length)
        : pathname;
    const [firstSegment] = withoutSitePath.split("/").filter(Boolean);
    if (!firstSegment?.startsWith("~")) {
        return "";
    }
    return firstSegment;
}

function mergeFilterParams(searchParams: URLSearchParams, filterParams: URLSearchParams): void {
    const staleKeys = Array.from(searchParams.keys()).filter(
        (key) => TRACKED_FILTER_KEYS.has(key) || key.startsWith("c.") || key.startsWith("d.") || key.startsWith("s."),
    );
    staleKeys.forEach((key) => searchParams.delete(key));
    filterParams.forEach((value, key) => searchParams.set(key, value));
}

function copyFilterState(filterState: FilterState): FilterState {
    return {
        ...filterState,
        customProperties: filterState.customProperties ? { ...filterState.customProperties } : undefined,
        defaultCustomProps: filterState.defaultCustomProps ? { ...filterState.defaultCustomProps } : undefined,
    };
}

/**
 * Compose a site-file navigation href the way authored AppLink does, without
 * an `inBookOutlet` gate: theme chrome is outside BookOutlet and must still
 * keep `book` when the book is already serialized to the URL.
 */
export function buildVerifiedLinkHref(params: BuildVerifiedLinkHrefParams): string | null {
    const targetPath = normalizeSitePath(params.targetPath);
    if (!targetPath) {
        return null;
    }
    const effectiveSitePath = params.isCustomDomain ? "" : (params.sitePath || "");
    const layoutSegment = getCurrentLayoutSegment(params.pathname, effectiveSitePath);
    const linkedState = copyFilterState(params.filterState);
    if (params.site) {
        applyDefaults(linkedState, params.site);
    }
    const searchParams = new URLSearchParams(params.search);
    mergeFilterParams(searchParams, getStateAsLocationQuery(linkedState));
    const resolvedBook = params.bookSerializeToUrl ? (params.bookPath || "") : "";
    if (resolvedBook) {
        searchParams.set("book", resolvedBook);
    }
    const hrefPath = layoutSegment
        ? `${effectiveSitePath}/${layoutSegment}${targetPath}`
        : `${effectiveSitePath}${targetPath}`;
    return joinPathSearchAndHash(hrefPath, searchParams.toString(), "");
}

export function isSameVerifiedLinkTopic(
    currentPath: string | null | undefined,
    targetPath: string,
): boolean {
    const current = normalizeSitePath(currentPath);
    const target = normalizeSitePath(targetPath);
    return current != null && target != null && current === target;
}
